import React, { useMemo } from "react";
import { BalaoComentarios } from "../../components/HistoricoAmbiente";
import { FiTruck } from "react-icons/fi";
import {
  ColumnResizeProvider,
  ResetColumnsButton,
  ResizableColgroup,
  ResizableTh,
} from "../../components/ResizableColumns";
import {
  CardItem,
  CardList,
  CardMeta,
  CardSub,
  CardTitulo,
  CardTopo,
} from "../../components/ListaCompacta/styles";
import useMediaQuery from "../../hooks/useMediaQuery";
import { mq } from "../../styles/canonical/breakpoints";
import { formatarData, formatarMoeda } from "../../utils/calculos";
import { filtrarColunasPorPrecos } from "../../utils/colunas";
import {
  AmbienteCard,
  AmbienteCardCliente,
  AmbienteCardHeader,
  AmbienteCardItens,
  AmbienteCardMeta,
  AmbienteCardNome,
  AmbienteCardRight,
  AmbienteCardValor,
  AmbientesList,
  ClickableRow,
  ClienteAmbiente,
  ClienteCelula,
  ClienteNome,
  EmptyText,
  ItemBadge,
  ItemChip,
  LocalTag,
  StatusBadge,
  Tabela,
  Td,
  Th,
} from "./styles";

const EMPTY_VALUE = "—";
const HIDDEN_VALUE_LABEL = "******";

function formatarDias(value) {
  if (value == null || !Number.isFinite(Number(value))) return EMPTY_VALUE;
  return `${new Intl.NumberFormat("pt-BR", { maximumFractionDigits: 0 }).format(
    Number(value)
  )} du`;
}

function MoneyCell({ value, precosVisiveis }) {
  return precosVisiveis ? formatarMoeda(value) : HIDDEN_VALUE_LABEL;
}

function CarregadoTag({ data }) {
  if (!data) return null;
  return (
    <LocalTag title="Ambiente já carregado: o item vai direto para a obra">
      <FiTruck size={11} aria-hidden />
      Carregado {formatarData(data)}
    </LocalTag>
  );
}


// Na meta do card a data sai com ano de 2 dígitos ("10/11/26").
function dataCurta(value) {
  const texto = formatarData(value);
  return typeof texto === "string" && texto
    ? texto.replace(/\/(\d{2})(\d{2})$/, "/$2")
    : EMPTY_VALUE;
}

function MetaPares({ pares }) {
  const visiveis = pares.filter((par) => par && par.valor != null && par.valor !== "");
  return (
    <CardMeta>
      {visiveis.map((par, i) => (
        <span className="par" key={par.k}>
          {i > 0 && <span className="sep" aria-hidden="true">· </span>}
          <span className="k">{par.k}</span> <b>{par.valor}</b>
        </span>
      ))}
    </CardMeta>
  );
}

function tratarTecla(event, abrir) {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    abrir();
  }
}

// Celular/tablet: um card por item; toque abre o mesmo drawer da linha.
function ItemsCards({ linhas, onSelectAmbiente }) {
  return (
    <CardList>
      {linhas.map((linha) => {
        const abrir = () => onSelectAmbiente?.(linha);
        return (
          <CardItem
            key={linha.key}
            $barra={linha.statusVariant}
            $clicavel
            role="button"
            tabIndex={0}
            onClick={abrir}
            onKeyDown={(event) => tratarTecla(event, abrir)}
          >
            <CardTopo>
              <CardTitulo title={linha.cliente}>{linha.cliente}</CardTitulo>
              <span className="selo">
                <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>
              </span>
            </CardTopo>
            <CardSub title={linha.ambiente}>
            {linha.ambiente}
            <BalaoComentarios row={linha.row} />
          </CardSub>
            <MetaPares
              pares={[
                linha.numeroLote !== EMPTY_VALUE && { k: "lote", valor: linha.numeroLote },
                { k: "item", valor: linha.item.label },
                linha.descricao !== EMPTY_VALUE && { k: "desc.", valor: linha.descricao },
                { k: "fila", valor: formatarDias(linha.diasParado) },
                { k: "prazo", valor: dataCurta(linha.prazo) },
                linha.carregadoEm && { k: "carregado", valor: dataCurta(linha.carregadoEm) },
              ]}
            />
          </CardItem>
        );
      })}
    </CardList>
  );
}

function AmbientesCards({ ambientes, precosVisiveis, onSelectAmbiente }) {
  return (
    <CardList>
      {ambientes.map((amb) => {
        const abrir = () => onSelectAmbiente?.(amb);
        const qtd = amb.itens.length;
        return (
          <CardItem
            key={amb.ambienteIndex}
            $barra={amb.statusVariant}
            $clicavel
            role="button"
            tabIndex={0}
            onClick={abrir}
            onKeyDown={(event) => tratarTecla(event, abrir)}
          >
            <CardTopo>
              <CardTitulo title={amb.cliente}>{amb.cliente}</CardTitulo>
              <span className="selo">
                <StatusBadge $tone={amb.statusVariant}>{amb.status}</StatusBadge>
              </span>
            </CardTopo>
            <CardSub title={amb.ambiente}>
                {amb.ambiente}
                <BalaoComentarios row={amb.row} />
              </CardSub>
            <MetaPares
              pares={[
                amb.numeroLote && amb.numeroLote !== EMPTY_VALUE && {
                  k: "lote",
                  valor: amb.numeroLote,
                },
                { k: qtd === 1 ? "item" : "itens", valor: qtd },
                amb.diasParado != null && { k: "fila", valor: formatarDias(amb.diasParado) },
                { k: "prazo", valor: dataCurta(amb.prazo) },
                amb.carregadoEm && { k: "carregado", valor: dataCurta(amb.carregadoEm) },
                precosVisiveis && { k: "valor", valor: formatarMoeda(amb.valor) },
              ]}
            />
          </CardItem>
        );
      })}
    </CardList>
  );
}

const COLS_TERCEIROS_ITENS = [
  { key: "clienteAmb", defaultWidth: null },
  { key: "lote", defaultWidth: "64px" },
  { key: "item", defaultWidth: "150px" },
  { key: "descricao", defaultWidth: null },
  { key: "diasFila", defaultWidth: "96px" },
  { key: "prazo", defaultWidth: "112px" },
  { key: "status", defaultWidth: "150px" },
  { key: "valor", defaultWidth: "120px" },
];

export function ItemsView({ linhas, precosVisiveis, onSelectAmbiente, emptyMessage }) {
  const cols = useMemo(
    () => filtrarColunasPorPrecos(COLS_TERCEIROS_ITENS, precosVisiveis),
    [precosVisiveis],
  );
  const compacto = useMediaQuery(mq.tablet);
  if (linhas.length === 0) {
    return <EmptyText>{emptyMessage}</EmptyText>;
  }
  if (compacto) {
    return <ItemsCards linhas={linhas} onSelectAmbiente={onSelectAmbiente} />;
  }

  return (
    <ColumnResizeProvider
      tableId="terceiros-itens"
      persistirNoBackend
      columns={cols}
    >
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 4 }}>
        <ResetColumnsButton />
      </div>
      <Tabela $density="compact">
        <ResizableColgroup />
        <thead>
          <tr>
            <ResizableTh as={Th} columnKey="clienteAmb">Cliente / Ambiente</ResizableTh>
            <ResizableTh as={Th} columnKey="lote">Lote</ResizableTh>
            <ResizableTh as={Th} columnKey="item">Item pendente</ResizableTh>
            <ResizableTh as={Th} columnKey="descricao">Descrição / Quantidade</ResizableTh>
            <ResizableTh as={Th} columnKey="diasFila" $center>Na fila</ResizableTh>
            <ResizableTh as={Th} columnKey="prazo" title="Coluna ENTREGA TERCEIROS da planilha">
              Prazo terceiro
            </ResizableTh>
            <ResizableTh as={Th} columnKey="status" $center>Status</ResizableTh>
            {precosVisiveis && (
              <ResizableTh as={Th} columnKey="valor" $right>Valor do ambiente</ResizableTh>
            )}
          </tr>
        </thead>
        <tbody>
          {linhas.map((linha) => (
            <ClickableRow key={linha.key} onClick={() => onSelectAmbiente?.(linha)}>
              <Td>
                {linha.primeiroDoAmbiente ? (
                  <ClienteCelula>
                    <ClienteNome>{linha.cliente}</ClienteNome>
                    <ClienteAmbiente>
                  {linha.ambiente}
                  <BalaoComentarios row={linha.row} />
                </ClienteAmbiente>
                    <CarregadoTag data={linha.carregadoEm} />
                  </ClienteCelula>
                ) : (
                  <ClienteCelula>
                    <ClienteAmbiente>
                  ↳ {linha.ambiente}
                  <BalaoComentarios row={linha.row} />
                </ClienteAmbiente>
                  </ClienteCelula>
                )}
              </Td>
              <Td $muted={linha.numeroLote === EMPTY_VALUE}>
                {linha.primeiroDoAmbiente ? linha.numeroLote : ""}
              </Td>
              <Td>
                <ItemBadge>{linha.item.label}</ItemBadge>
              </Td>
              <Td $wrap $muted={linha.descricao === EMPTY_VALUE}>
                {linha.descricao}
              </Td>
              <Td $center>{formatarDias(linha.diasParado)}</Td>
              <Td>{formatarData(linha.prazo) || EMPTY_VALUE}</Td>
              <Td $center>
                <StatusBadge $tone={linha.statusVariant}>{linha.status}</StatusBadge>
              </Td>
              {precosVisiveis && (
                <Td $right>
                  {linha.primeiroDoAmbiente ? (
                    <MoneyCell value={linha.valor} precosVisiveis={precosVisiveis} />
                  ) : (
                    ""
                  )}
                </Td>
              )}
            </ClickableRow>
          ))}
        </tbody>
      </Tabela>
    </ColumnResizeProvider>
  );
}

function AmbienteCardView({ amb, precosVisiveis, onSelectAmbiente }) {
  return (
    <AmbienteCard
      type="button"
      $tone={amb.statusVariant}
      onClick={() => onSelectAmbiente?.(amb)}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
        <AmbienteCardHeader>
          <AmbienteCardCliente>{amb.cliente}</AmbienteCardCliente>
          <span aria-hidden style={{ opacity: 0.5 }}>·</span>
          <AmbienteCardNome>
                {amb.ambiente}
                <BalaoComentarios row={amb.row} />
              </AmbienteCardNome>
          {amb.numeroLote && amb.numeroLote !== EMPTY_VALUE && (
            <ItemBadge>Lote {amb.numeroLote}</ItemBadge>
          )}
        </AmbienteCardHeader>
        <AmbienteCardMeta>
          {amb.diasParado != null && (
            <>
              <span>Na fila há {formatarDias(amb.diasParado)}</span>
              <span aria-hidden style={{ opacity: 0.5 }}>·</span>
            </>
          )}
          <span>Prazo terceiro: {formatarData(amb.prazo) || EMPTY_VALUE}</span>
          <span aria-hidden style={{ opacity: 0.5 }}>·</span>
          <span>
            {amb.itens.length} {amb.itens.length === 1 ? "item pendente" : "itens pendentes"}
          </span>
        </AmbienteCardMeta>
        <CarregadoTag data={amb.carregadoEm} />
        <AmbienteCardItens>
          {amb.itens.map((item) => (
            <ItemChip key={item.key} $tone={item.statusVariant}>
              <strong>{item.item.label}</strong>
              <span>{item.descricao !== EMPTY_VALUE ? item.descricao : EMPTY_VALUE}</span>
            </ItemChip>
          ))}
        </AmbienteCardItens>
      </div>
      <AmbienteCardRight>
        <StatusBadge $tone={amb.statusVariant}>{amb.status}</StatusBadge>
        <AmbienteCardValor>
          <MoneyCell value={amb.valor} precosVisiveis={precosVisiveis} />
        </AmbienteCardValor>
      </AmbienteCardRight>
    </AmbienteCard>
  );
}

export function AmbientesView({ ambientes, precosVisiveis, onSelectAmbiente, emptyMessage }) {
  const compacto = useMediaQuery(mq.tablet);
  if (ambientes.length === 0) {
    return <EmptyText>{emptyMessage}</EmptyText>;
  }
  if (compacto) {
    return (
      <AmbientesCards
        ambientes={ambientes}
        precosVisiveis={precosVisiveis}
        onSelectAmbiente={onSelectAmbiente}
      />
    );
  }

  return (
    <AmbientesList>
      {ambientes.map((amb) => (
        <AmbienteCardView
          key={amb.ambienteIndex}
          amb={amb}
          precosVisiveis={precosVisiveis}
          onSelectAmbiente={onSelectAmbiente}
        />
      ))}
    </AmbientesList>
  );
}
